import moment from 'moment';
import api from '@/common/api';
import { findWalletStatistics, parseWalletStatistics } from '@/common/models/walletStatistics';

// Период для колонки «Израсходовано за 30 дней» в списке корпов.
const SPENT_PERIOD_DAYS = 30;
// Номер последнего запроса списка: ответ устаревшего запроса (фильтр уже сменили) не применяем.
let lastListRequestId = 0;
// Последний запрошенный корп для карточки: ответ по другому корпу не применяем.
let lastClientRequestId = null;

const initialState = () => ({
  loading: {
    load: false,
    create: false,
    edit: false,
    loadResults: false,
    client: false,
    editMail: false,
    wallet: false,
  },
  errors: {
    load: [],
    create: [],
    loadResults: [],
    client: [],
    edit: [],
    wallet: null,
  },
  count: 0,
  clients: [],
  client: null,
  // Сводка кошелька корпа (модель адаптера) за выбранный период и сам период.
  wallet: null,
  walletPeriod: { from: null, to: null },
  walletCorpId: null,
  selected: [],
  editedClient: null,
  dialogCreateShow: false,
  dialogEditShow: false,

  filter: {
    page: 1,
    page_size: 25,
    first_name: '',
    last_name: '',
    patronymic: '',
    email: '',
    phone: '',
    telegram: '',
    company: '',
    supervisor: '',
    market_sector: '',
    taxpayer_id_number: '',
    // Срок кабинета (фильтры бэка): статус и диапазон «Активен до», границы включены
    activity_status: '',
    active_until_from: '',
    active_until_to: '',
    // Теги (только админ): ID выбранных тегов, клиент подходит, если у него есть хотя бы один из них
    tags: [],
  },

  // Отдельно от filter/clients выше — под поиск в выпадающем списке (ExpertFormDialog
  // календаря записи): нужен полнотекстовый поиск по имени без пагинации, а не постраничный
  // список раздела «Корп. клиенты» с фильтром строго по first_name/last_name.
  bookingSearch: {
    loading: false,
    results: [],
  },
});

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async loadClients({ commit }) {
      commit('loadClientsRequest');
      try {
        const response = await api.get('/users/corp_clients/?page_size=-1', { timeout: 120000 });
        commit('setClients', response);
        commit('setCount', response.length);
        commit('loadClientsSuccess');
      } catch (err) {
        commit('loadClientsFailure', Object.entries(err.data));
        throw err;
      }
    },
    async loadClientsFilter({ commit, getters }) {
      lastListRequestId += 1;
      const requestId = lastListRequestId;
      commit('loadClientsRequest');
      try {
        let filter = '';
        Object.keys(getters.filter).forEach((key) => {
          const value = getters.filter[key];
          // Теги уходят одним параметром tags=41,73; пустой выбор — параметра нет вовсе
          if (Array.isArray(value)) {
            if (value.length) filter += `${filter ? '&' : ''}${key}=${value.join(',')}`;
          } else if (value) {
            if (filter) filter += '&';
            filter += `${key}=${encodeURIComponent(value)}`;
          }
        });
        // from_date/to_date ограничивают только обороты wallet_statistics, состав списка и остатки не меняются.
        const toDate = moment().format('YYYY-MM-DD');
        const fromDate = moment().subtract(SPENT_PERIOD_DAYS - 1, 'days').format('YYYY-MM-DD');
        filter += `${filter ? '&' : ''}from_date=${fromDate}&to_date=${toDate}`;
        const response = await api.get(`/users/corp_clients/?${filter}`, { timeout: 120000 });
        if (requestId !== lastListRequestId) return response;
        commit('setClients', response.results);
        commit('setCount', response.count);
        commit('loadClientsSuccess');
        return response;
      } catch (err) {
        if (requestId !== lastListRequestId) return null;
        commit('loadClientsFailure', Object.entries(err.data));
        throw err;
      }
    },
    // name — новый параметр бэка (полное совпадение по ФИО, добавлен 05.09.2026), отдельный
    // от старых first_name/last_name, которые остаются для фильтра в разделе «Корп. клиенты».
    async searchClientsForBooking({ commit }, name) {
      commit('setBookingSearchLoading', true);
      try {
        const params = { page_size: -1 };
        if (name) params.name = name;
        const response = await api.get('/users/corp_clients/', { params, timeout: 120000 });
        commit('setBookingSearchResults', Array.isArray(response) ? response : []);
      } catch (err) {
        commit('setBookingSearchResults', []);
      } finally {
        commit('setBookingSearchLoading', false);
      }
    },

    async loadClientsResults({ commit, getters }) {
      commit('loadClientsResultsRequest');
      const data = {
        candidates: getters.selected,
      };
      try {
        const response = await api.post('/users/candidates/get-test-result/', data);
        commit('loadClientsResultsSuccess');
        return response;
      } catch (err) {
        commit('loadClientsResultsFailure', Object.entries(err.data));
        throw err;
      }
    },
    async createClient({ commit, dispatch }, data) {
      commit('createClientRequest');
      if (!data.promocode) {
        delete data.promocode;
      }
      try {
        // Корп создаётся без технического менеджера, менеджеров добавляют отдельно.
        const response = await api.post('/users/corp_clients/', data);
        commit('createClientSuccess');
        dispatch('loadClientsFilter');
        return response;
      } catch (err) {
        commit('createClientFailure', Object.entries(err.data));
        throw err;
      }
    },
    // Сводка кошелька корпа за период: from/to ограничивают только обороты, остатки — на текущий момент.
    // self — корп смотрит свою карточку: его блок приходит в get-me (отчёт бэка, 3.1).
    async loadClientWallet({ commit, state }, {
      id, from, to, self = false,
    }) {
      if (String(state.walletCorpId) !== String(id)) commit('loadWalletSuccess', null);
      commit('setWalletPeriod', { from, to });
      commit('setWalletCorpId', id);
      commit('loadWalletRequest');
      const isStale = () => String(state.walletCorpId) !== String(id)
        || state.walletPeriod.from !== from || state.walletPeriod.to !== to;
      try {
        const params = {};
        if (from) params.from_date = from;
        if (to) params.to_date = to;
        let raw = null;
        if (self) {
          const me = await api.get('/users/get-me', { params });
          raw = findWalletStatistics(me);
        }
        if (!raw) {
          const response = await api.get(`/users/corp_clients/${id}/`, { params });
          raw = response.wallet_statistics;
        }
        // Пока ждали ответ, могли сменить корп или период — устаревший ответ не применяем.
        if (isStale()) return;
        commit('loadWalletSuccess', parseWalletStatistics(raw));
      } catch (err) {
        if (isStale()) return;
        // Для экрана важен только вид ошибки: 403 — «нет доступа», остальное — «не удалось загрузить».
        commit('loadWalletFailure', { status: (err && err.status) || null });
      }
    },
    async loadClient({ commit, state }, id) {
      // Открыли другой корп — прежний не показываем, пока грузится новый.
      if (state.client && String(state.client.id) !== String(id)) commit('loadClientSuccess', null);
      lastClientRequestId = String(id);
      commit('loadClientRequest');
      try {
        const response = await api.get(`/users/corp_clients/${id}/`);
        if (lastClientRequestId !== String(id)) return;
        commit('loadClientSuccess', response);
      } catch (err) {
        if (lastClientRequestId !== String(id)) return;
        commit('loadClientFailure', Object.entries(err.data));
        throw err;
      }
    },
    async saveClient({ commit, dispatch }, data) {
      commit('editClientRequest');
      try {
        await api.patch(`/users/corp_clients/${data.id}/`, data);
        commit('editClientSuccess');
        dispatch('loadClientsFilter');
        dispatch('loadClient', data.id);
      } catch (err) {
        commit('editClientFailure', Object.entries(err.data));
        throw err;
      }
    },
    clearError({ commit }) {
      commit('clearError');
    },
    setSelected({ commit }, id) {
      commit('setSelected', id);
    },
    allSelected({ commit }) {
      commit('allSelected');
    },
    clearSelected({ commit }) {
      commit('clearSelected');
    },
    setDialogCreateShow({ commit }, bool) {
      if (!bool) commit('setEditedClient', null);
      commit('setDialogCreateShow', bool);
    },
    setDialogEditShow({ commit }, bool) {
      if (!bool) commit('setEditedClient', null);
      commit('setDialogEditShow', bool);
    },
    async setFilterPage({ commit, dispatch }, page) {
      commit('setFilterPage', page);
      await dispatch('loadClientsFilter');
    },
    async setFilterPageSize({ commit, dispatch }, payload) {
      commit('setFilterPage', payload.page);
      commit('setFilterPageSize', payload.pageSize);
      await dispatch('loadClientsFilter');
    },
    async setFilterFirstName({ commit, dispatch }, value) {
      commit('setFilterField', { key: 'first_name', value });
      commit('setFilterPage', 1);
      await dispatch('loadClientsFilter');
    },
    async setFilterLastName({ commit, dispatch }, value) {
      commit('setFilterField', { key: 'last_name', value });
      commit('setFilterPage', 1);
      await dispatch('loadClientsFilter');
    },
    async setFilterCompany({ commit, dispatch }, value) {
      commit('setFilterField', { key: 'company', value });
      commit('setFilterPage', 1);
      await dispatch('loadClientsFilter');
    },
    async setFilterEmail({ commit, dispatch }, value) {
      commit('setFilterField', { key: 'email', value });
      commit('setFilterPage', 1);
      await dispatch('loadClientsFilter');
    },
    async setFilterValue({ commit, dispatch }, { key, value }) {
      commit('setFilterField', { key, value });
      commit('setFilterPage', 1);
      await dispatch('loadClientsFilter');
    },
    async clearFilterAll({ commit, dispatch }) {
      ['first_name', 'last_name', 'company', 'email', 'taxpayer_id_number', 'supervisor', 'activity_status', 'active_until_from', 'active_until_to'].forEach((key) => {
        commit('setFilterField', { key, value: '' });
      });
      commit('setFilterField', { key: 'tags', value: [] });
      commit('setFilterPage', 1);
      await dispatch('loadClientsFilter');
    },
    async setClientSendMail({ commit }, data) {
      commit('editMailRequest');
      try {
        const response = await api.patch(`/users/corp_clients/${data.id}/`, data);
        commit('editMailSuccess');
        commit('setSendMail', response);
      } catch (err) {
        commit('editMailFailure', Object.entries(err.data));
        throw err;
      }
    },

  },
  mutations: {
    loadClientsRequest(state) {
      state.loading.load = true;
      state.errors.load = [];
    },
    loadClientsSuccess(state) {
      state.loading.load = false;
    },
    loadClientsFailure(state, error) {
      state.errors.load = error;
      state.loading.load = false;
    },

    loadClientsResultsRequest(state) {
      state.loading.loadResults = true;
      state.errors.loadResults = [];
    },
    loadClientsResultsSuccess(state) {
      state.loading.loadResults = false;
    },
    loadClientsResultsFailure(state, error) {
      state.errors.loadResults = error;
      state.loading.loadResults = false;
    },

    createClientRequest(state) {
      state.loading.create = true;
      state.errors.create = [];
    },
    createClientSuccess(state) {
      state.loading.create = false;
    },
    createClientFailure(state, error) {
      state.errors.create = error;
      state.loading.create = false;
    },

    loadClientRequest(state) {
      state.loading.client = true;
      state.errors.client = [];
    },
    loadClientSuccess(state, client) {
      state.client = client;
      state.loading.client = false;
    },
    loadClientFailure(state, error) {
      state.errors.client = error;
      state.loading.client = false;
    },

    setWalletPeriod(state, period) {
      state.walletPeriod = period;
    },
    setWalletCorpId(state, id) {
      state.walletCorpId = id;
    },
    loadWalletRequest(state) {
      state.loading.wallet = true;
      state.errors.wallet = null;
    },
    loadWalletSuccess(state, wallet) {
      state.wallet = wallet;
      state.loading.wallet = false;
    },
    loadWalletFailure(state, error) {
      state.wallet = null;
      state.errors.wallet = error;
      state.loading.wallet = false;
    },

    editClientRequest(state) {
      state.loading.edit = true;
      state.errors.edit = [];
    },
    editClientSuccess(state) {
      state.loading.edit = false;
    },
    editClientFailure(state, error) {
      state.errors.edit = error;
      state.loading.edit = false;
    },

    setCount(state, count) {
      state.count = count;
    },
    clearSelected(state) {
      state.selected = [];
    },
    clearError(state) {
      state.errors = initialState().errors;
    },
    setClients(state, clients) {
      state.clients = clients;
    },
    setSelected(state, id) {
      if (id || id === 0) {
        const itemFound = state.selected.some((item) => item === id);
        if (itemFound) {
          state.selected = state.selected.filter((item) => item !== id);
        } else {
          state.selected.push(id);
        }
      } else {
        state.selected = [];
      }
    },
    allSelected(state) {
      state.clients.forEach((c) => {
        if (!state.selected.some((id) => c.id === id)) {
          state.selected.push(c.id);
        }
      });
    },
    setEditedClient(state, client) {
      state.editedClient = client;
    },
    setDialogCreateShow(state, bool) {
      state.dialogCreateShow = bool;
    },
    setDialogEditShow(state, bool) {
      state.dialogEditShow = bool;
    },
    setFilterPage(state, page) {
      state.filter.page = parseInt(page, 10);
    },
    setFilterPageSize(state, pageSize) {
      state.filter.page_size = parseInt(pageSize, 10);
    },
    setFilterField(state, { key, value }) {
      state.filter[key] = value;
    },
    setBookingSearchLoading(state, value) {
      state.bookingSearch.loading = value;
    },
    setBookingSearchResults(state, results) {
      state.bookingSearch.results = results;
    },

    setSendMail(state, data) {
      state.client.send_mail = data.send_mail;
    },
    editMailRequest(state) {
      state.loading.editMail = true;
      state.errors.edit = [];
    },
    editMailSuccess(state) {
      state.loading.editMail = false;
    },
    editMailFailure(state, error) {
      state.errors.edit = error;
      state.loading.editMail = false;
    },

  },
  getters: {
    loading: (state) => state.loading,
    errors: (state) => state.errors,
    count: (state) => state.count,
    clients: (state) => state.clients,
    filter: (state) => state.filter,
    client: (state) => state.client,
    wallet: (state) => state.wallet,
    walletPeriod: (state) => state.walletPeriod,
    selected: (state) => state.selected,
    editedClient: (state) => state.editedClient,
    dialogCreateShow: (state) => state.dialogCreateShow,
    dialogEditShow: (state) => state.dialogEditShow,
    bookingSearchResults: (state) => state.bookingSearch.results,
    bookingSearchLoading: (state) => state.bookingSearch.loading,

  },
};
